/* eslint-disable max-lines */
import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  ExtraWorkedTimeRequestStatus,
  ExtraWorkedTimeRequestStatusUpdater,
} from "@src/appV2/Shifts/MandatoryBreakPolicy/api/useExtraWorkedTimeRequests";
import { ExtraWorkedTimeType } from "@src/appV2/Shifts/Shift/types";
import {
  type QueryFunctionContext,
  type QueryKey,
  useInfiniteQuery,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { cancellationDetailsSchema } from "../../Support/types";

const WorkplaceIdentifier = z.object({
  id: z.string(),
  type: z.literal("workplace"),
});

const WorkerIdentifier = z.object({
  id: z.string(),
  type: z.literal("worker"),
});

const ExtraWorkedTimeRequestIdentifier = z.object({
  id: z.string(),
  type: z.literal("extra-worked-time-request"),
});

// Request schemas
const ShiftListFilterSchema = z
  .object({
    id: z.string().optional(),
    facilityId: z.string().optional(),
    agentId: z.string().optional(),
    timeSlot: z.string().optional(),
    qualificationName: z.string().optional(),
    start: z
      .object({
        gt: z.coerce.date().optional(),
        lt: z.coerce.date().optional(),
      })
      .optional(),
    end: z
      .object({
        lt: z.coerce.date().optional(),
        gte: z.coerce.date().optional(),
      })
      .optional(),
    deleted: z.boolean().optional(),
  })
  .optional();

const ShiftListFieldsSchema = z
  .object({
    shift: z.array(z.string()).optional(),
    workplace: z.array(z.string()).optional(),
    worker: z.array(z.string()).optional(),
    shiftLog: z.array(z.string()).optional(),
    extraWorkedTimeRequest: z.array(z.string()).optional(),
  })
  .optional();

const ShiftListSortSchema = z
  .object({
    field: z.enum([
      "schedule.startAt",
      "schedule.endAt",
      "pay.total",
      "createdAt",
      "clockInOut.start",
      "clockInOut.end",
    ]),
    order: z.enum(["asc", "desc"]).default("desc"),
  })
  .optional()
  .default({
    field: "schedule.startAt",
    order: "desc",
  });

const ShiftListPageSchema = z
  .object({
    size: z.coerce.number().min(1).max(100).default(20),
    cursor: z.string().optional(),
  })
  .optional();

const GetShiftsQuerySchema = z.object({
  filter: ShiftListFilterSchema,
  fields: ShiftListFieldsSchema,
  include: z
    .array(z.enum(["workplace", "worker", "shift-log", "extra-worked-time-request"]))
    .optional(),
  sort: ShiftListSortSchema,
  page: ShiftListPageSchema,
});

// Response schemas
const ShiftAttributesSchema = z
  .object({
    schedule: z
      .object({
        startAt: z.string().optional(),
        endAt: z.string().optional(),
        timeSlot: z.string().optional(),
        durationWithoutBreaksInHours: z.number().optional(),
      })
      .optional(),
    requirements: z
      .object({
        qualificationName: z.string().optional(),
      })
      .optional(),
    pay: z
      .object({
        total: z.number().optional(),
      })
      .optional(),
    clockInOut: z
      .object({
        start: z.string().optional(),
        end: z.string().optional(),
      })
      .optional(),
    lunchInOut: z
      .object({
        start: z.string().optional(),
        end: z.string().optional(),
      })
      .optional(),
    refillInfo: z
      .object({
        urgencyLevel: z.number().optional(),
        previousWorker: z
          .object({
            userId: z.string(),
            name: z.string(),
          })
          .optional(),
        isUrgentlyBooked: z.boolean().optional(),
      })
      .optional(),
    note: z.string().optional(),
    verified: z.boolean().optional(),
    isMissedPunchRequestEnabled: z.boolean().optional(),
    timecard: z
      .object({
        files: z
          .array(
            z.object({
              url: z.string().optional(),
              filename: z.string(),
              fileStorageFileKey: z.string().optional(),
              fileStorageRootFolder: z.string().optional(),
              pdfFileStorageFileKey: z.string().optional(),
              timesheet: z.string().optional(),
              signedType: z.string().optional(),
              digitallySignedBy: z.string().optional(),
            })
          )
          .optional(),
      })
      .optional(),
    shiftEndGraceTimeHours: z.number().min(0).optional(),
    deleted: z.boolean().optional(),
    cancellationDetails: cancellationDetailsSchema.optional(),
  })
  .optional();

const ShiftRelationshipsSchema = z
  .object({
    workplace: z
      .object({
        data: WorkplaceIdentifier,
      })
      .optional(),
    worker: z
      .object({
        data: WorkerIdentifier,
      })
      .optional(),
    extraWorkedTimeRequest: z
      .object({
        data: z.array(ExtraWorkedTimeRequestIdentifier),
      })
      .optional(),
  })
  .optional();

const ShiftDataSchema = z.object({
  id: z.string(),
  type: z.literal("shift"),
  attributes: ShiftAttributesSchema,
  relationships: ShiftRelationshipsSchema,
});

const WorkplaceDataSchema = z.object({
  id: z.string(),
  type: z.literal("workplace"),
  attributes: z
    .object({
      name: z.string().optional(),
      timezone: z.string().optional(),
      geoLocation: z
        .object({
          coordinates: z.tuple([z.number(), z.number()]),
          type: z.literal("Point"),
        })
        .optional(),
    })
    .optional(),
});

const WorkerDataSchema = z.object({
  id: z.string(),
  type: z.literal("worker"),
  attributes: z
    .object({
      name: z.string().optional(),
    })
    .optional(),
});

const ExtraWorkedTimeRequestDataSchema = z.object({
  id: z.string(),
  type: z.literal("extra-worked-time-request"),
  attributes: z
    .object({
      type: z.nativeEnum(ExtraWorkedTimeType),
      status: z.nativeEnum(ExtraWorkedTimeRequestStatus),
      timeRequestedInMinutes: z.number().optional(),
      statusUpdatedBy: z.nativeEnum(ExtraWorkedTimeRequestStatusUpdater).optional(),
      isFullShiftPayment: z.boolean().optional(),
      createdAt: z.string().optional(),
      start: z.string().optional(),
      end: z.string().optional(),
    })
    .optional(),
  meta: z
    .object({
      isNotificationDismissed: z.boolean().optional(),
      isOutcomeAcknowledged: z.boolean().optional(),
    })
    .optional(),
});

const ShiftListIncludedSchema = z
  .array(
    z.discriminatedUnion("type", [
      WorkplaceDataSchema,
      WorkerDataSchema,
      ExtraWorkedTimeRequestDataSchema,
    ])
  )
  .optional();

const ShiftListMetaSchema = z
  .object({
    totalCount: z.number(),
  })
  .optional();

const GetShiftsResponseSchema = z.object({
  data: z.array(ShiftDataSchema),
  included: ShiftListIncludedSchema,
  meta: ShiftListMetaSchema,
});

export type GetShiftsRequest = z.infer<typeof GetShiftsQuerySchema>;
export type GetShiftsResponse = z.infer<typeof GetShiftsResponseSchema>;
export type ShiftType = z.infer<typeof ShiftDataSchema>;
export type ExtraWorkedTimeRequestType = z.infer<typeof ExtraWorkedTimeRequestDataSchema>;

export interface UsePaginatedWorkerShiftsParams {
  workerId: string;
  workplaceId?: string;
  timeline?: "PAST" | "FUTURE" | "IN_PROGRESS" | "PAST_AND_IN_PROGRESS";
  sortBy?:
    | "schedule.startAt"
    | "schedule.endAt"
    | "pay.total"
    | "createdAt"
    | "clockInOut.start"
    | "clockInOut.end";
  sortOrder?: "asc" | "desc";
  pageSize?: number;
  fields?: {
    shift?: string[];
    workplace?: string[];
    extraWorkedTimeRequest?: string[];
  };
  include?: Array<"workplace" | "worker" | "shift-log" | "extra-worked-time-request">;
  filter?: {
    id?: string;
    facilityId?: string;
    agentId?: string;
    timeSlot?: string;
    qualificationName?: string;
    start?: {
      gt?: Date;
      lt?: Date;
    };
    end?: {
      lt?: Date;
      gte?: Date;
    };
    deleted?: boolean;
  };
}

export interface UsePaginatedWorkerShiftsOptions {
  enabled?: boolean;
  staleTime?: number;
  cacheTime?: number;
  refetchOnMount?: boolean | "always";
  userErrorMessage?: string;
}

const DEFAULT_PAGE_SIZE = 20;

export const GET_PAGINATED_WORKER_SHIFTS_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/v3/shifts`;

export async function fetchWorkerShifts(
  props: UsePaginatedWorkerShiftsParams & { cursor?: string }
): Promise<GetShiftsResponse> {
  const {
    workerId,
    workplaceId,
    timeline,
    sortBy = "schedule.startAt",
    sortOrder = "desc",
    pageSize = DEFAULT_PAGE_SIZE,
    fields = {
      shift: [
        "schedule.startAt",
        "schedule.endAt",
        "requirements.qualificationName",
        "pay.total",
        "clockInOut.start",
        "clockInOut.end",
      ],
    },
    include = ["workplace"],
    filter,
    cursor,
  } = props;

  const queryFilter: UsePaginatedWorkerShiftsParams["filter"] = {
    agentId: workerId,
  };

  if (workplaceId) {
    queryFilter.facilityId = workplaceId;
  }

  switch (timeline) {
    case "PAST": {
      queryFilter.end = {
        lt: new Date(),
      };
      break;
    }

    case "FUTURE": {
      queryFilter.start = {
        gt: new Date(),
      };
      break;
    }

    case "PAST_AND_IN_PROGRESS": {
      queryFilter.start = {
        lt: new Date(),
      };
      break;
    }

    default: {
      break;
    }
  }

  if (filter?.timeSlot) {
    queryFilter.timeSlot = filter.timeSlot;
  }

  if (filter?.qualificationName) {
    queryFilter.qualificationName = filter.qualificationName;
  }

  // Add start and end date filters if provided
  if (filter?.start) {
    queryFilter.start = filter.start;
  }

  if (filter?.end) {
    queryFilter.end = filter.end;
  }

  if (isDefined(filter?.deleted)) {
    queryFilter.deleted = filter.deleted;
  }

  const queryParams: GetShiftsRequest = {
    filter: queryFilter,
    fields,
    include,
    sort: {
      field: sortBy,
      order: sortOrder,
    },
    page: {
      size: pageSize,
      ...(cursor && { cursor }),
    },
  };

  const response = await get({
    url: GET_PAGINATED_WORKER_SHIFTS_URL,
    queryParams,
    requestSchema: GetShiftsQuerySchema,
    responseSchema: GetShiftsResponseSchema,
  });

  return response.data;
}

export function usePaginatedWorkerShifts(
  params: UsePaginatedWorkerShiftsParams,
  options: UsePaginatedWorkerShiftsOptions = {}
): UseInfiniteQueryResult<GetShiftsResponse, AxiosError> {
  const {
    workerId,
    workplaceId,
    timeline,
    sortBy = "schedule.startAt",
    sortOrder = "desc",
    pageSize = DEFAULT_PAGE_SIZE,
    fields = {
      shift: [
        "schedule.startAt",
        "schedule.endAt",
        "requirements.qualificationName",
        "pay.total",
        "clockInOut.start",
        "clockInOut.end",
      ],
    },
    include = ["workplace"],
    filter,
  } = params;
  const { userErrorMessage, ...reactQueryOptions } = options;

  return useInfiniteQuery({
    queryKey: [
      "worker-shifts-v3",
      workerId,
      workplaceId,
      timeline,
      sortBy,
      sortOrder,
      pageSize,
      fields,
      include,
      filter,
    ],
    queryFn: async ({ pageParam: cursor }: QueryFunctionContext<QueryKey, string | undefined>) => {
      return await fetchWorkerShifts({
        ...params,
        cursor,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.meta?.totalCount) {
        const totalFetched = allPages.reduce((sum, page) => sum + page.data.length, 0);
        const totalAvailable = lastPage.meta.totalCount;

        if (totalFetched < totalAvailable) {
          return String(totalFetched);
        }
      }

      return undefined;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKPLACE_WORKERS_SHIFTS_FAILURE,
      userErrorMessage: userErrorMessage ?? "Something went wrong while loading shifts",
    },
    ...reactQueryOptions,
  });
}
/* eslint-enable max-lines */
